import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import axiosInstance from './axios';
import { getConfig } from './config';
import { getQueryClient } from './query-client';
import { APIResponse, Location, UpdateLocationRequest } from './types';
import { SessionScheduleFormInputType } from '~/modules/auth/components/BusinessSettings/components/SessionSchedule/components/SessionScheduleForm/helpers/SessionScheduleFormSchema';

export type PresignedUrlResponse = {
  fields: {
    AWSAccessKeyId: string;
    acl: string;
    key: string;
    policy: string;
    signature: string;
  };
  final_url: string;
  url: string;
};

export const getLocations = ({ gymId }: { gymId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<Location[]>>(`${config.domains.auth}/admin/locations`, { params: { gym_id: gymId } })
    .then(({ data }) => data?.data);
};
export const useGetLocationsQuery = ({ gymId }: { gymId: number | undefined }) =>
  useQuery({
    queryKey: ['locations'],
    queryFn: () => (gymId ? getLocations({ gymId }) : undefined),
    enabled: !!gymId,
  });

const getLocation = ({ locationId }: { locationId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<Location>>(`${config.domains.auth}/admin/locations/${locationId}`)
    .then(({ data }) => data?.data);
};

export const useGetLocationQuery = (params: { locationId: number }) =>
  useQuery({
    queryKey: ['locations', params],
    queryFn: () => getLocation(params),
  });

const updateBusinessSettings = (data: SessionScheduleFormInputType & { location_id: number }) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(`${config.domains.auth}/admin/locations/${data.location_id}`, data);
};

export const useUpdateBusinessLocationMutation = () =>
  useMutation({
    mutationFn: updateBusinessSettings,
    onSuccess: async (_, data) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['locations', { locationId: data.location_id }] });
    },
  });

const updateLocation = (data: UpdateLocationRequest) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(`${config.domains.auth}/admin/locations/${data.location_id}`, data);
};
export const useUpdateLocationMutation = () =>
  useMutation({
    mutationFn: updateLocation,
    onSuccess: async (response, data) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['locations', { locationId: data.location_id }] });
      await queryClient.refetchQueries({ queryKey: ['user'] });
      toast.success(response.data.message);
    },
  });

const deleteLocation = (data: Location) => {
  const config = getConfig();

  return axiosInstance.delete<APIResponse>(`${config.domains.auth}/admin/locations/${data.location_id}`);
};
export const useDeleteLocationMutation: any = () =>
  useMutation({
    mutationFn: deleteLocation,
    onSuccess: async (response) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['locations'] });
      toast.success(response.data.message);
    },
  });

const onboardMerchant = ({ locationId }: { locationId: number }, request: any) => {
  const config = getConfig();

  return axiosInstance.put<APIResponse>(`${config.domains.auth}/admin/locations/${locationId}/payrix_onboard`, request);
};
export const useOnboardMerchantMutation: any = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (request: any) => onboardMerchant(params, request),
    onSuccess: async (response) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['members', params] });
      toast.success(response.data.message);
    },
  });
